import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../utils/api';

// ─── SLOTS GAME ───────────────────────────────────────────────────────────────
const SLOT_SYMBOLS = [
  { sym: '🍒', name: 'Cherry',   mult: 2  },
  { sym: '🍋', name: 'Lemon',    mult: 2  },
  { sym: '🔔', name: 'Bell',     mult: 3  },
  { sym: '⭐', name: 'Star',     mult: 4  },
  { sym: '💎', name: 'Diamond',  mult: 8  },
  { sym: '🎰', name: 'Slots',    mult: 10 },
  { sym: '🍀', name: 'Clover',   mult: 5  },
  { sym: '7️⃣',  name: 'Seven',   mult: 50 },
];

function SlotsGame({ balance, onPlay, disabled }) {
  const [reels, setReels] = useState([['🍒','🍋','🔔'],['⭐','💎','🎰'],['🍀','7️⃣','🍒']]);
  const [spinning, setSpinning] = useState(false);
  const [bet, setBet] = useState(5);
  const [result, setResult] = useState(null);
  const [spinCount, setSpinCount] = useState(0);

  const spin = async () => {
    if (spinning || disabled || balance < bet) return;
    setSpinning(true); setResult(null);

    // Animate reels
    let ticks = 0;
    const interval = setInterval(() => {
      setReels([
        [rSym(), rSym(), rSym()],
        [rSym(), rSym(), rSym()],
        [rSym(), rSym(), rSym()],
      ]);
      ticks++;
      if (ticks >= 18) clearInterval(interval);
    }, 60);

    try {
      const { data } = await api.post('/games/play', { game: 'slots', betAmount: bet });
      setTimeout(() => {
        setReels(data.details.reels);
        setResult({ win: data.winAmount, type: data.details.winType, balance: data.session.currentBalance });
        setSpinning(false);
        setSpinCount(c => c + 1);
        onPlay(data);
      }, 1100);
    } catch (err) {
      clearInterval(interval);
      setResult({ error: err.response?.data?.message || 'Error' });
      setSpinning(false);
    }
  };

  const rSym = () => SLOT_SYMBOLS[Math.floor(Math.random() * SLOT_SYMBOLS.length)].sym;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      {/* Machine */}
      <div style={{ background: 'linear-gradient(180deg, #1a1400 0%, #0d0b00 100%)', border: '3px solid #f5c400', borderRadius: 20, padding: '24px 32px', boxShadow: '0 0 40px rgba(245,196,0,0.3)', width: '100%', maxWidth: 380 }}>
        {/* Screen */}
        <div style={{ background: '#000', border: '2px solid #333', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
            {reels.map((reel, ri) => (
              <div key={ri} style={{ background: '#111', borderRadius: 8, border: '1px solid #333', overflow: 'hidden', width: 80 }}>
                {reel.map((sym, si) => (
                  <div key={si} style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, borderBottom: si < 2 ? '1px solid #222' : 'none', background: si === 1 ? '#1a1400' : 'transparent', filter: si === 1 ? 'none' : 'brightness(0.35)', transition: spinning ? 'none' : 'all 0.1s' }}>
                    {sym}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* Win line indicator */}
          <div style={{ height: 2, background: 'rgba(245,196,0,0.4)', margin: '8px 0 0', borderRadius: 1 }} />
        </div>

        {/* Result */}
        <div style={{ minHeight: 36, textAlign: 'center', marginBottom: 12 }}>
          {result && !result.error && (
            <div style={{ fontFamily: 'var(--font-display)', fontSize: result.win > 0 ? 22 : 14, color: result.win > 0 ? '#f5c400' : '#666', animation: result.win > 0 ? 'pulse 0.5s ease' : 'none' }}>
              {result.win > 0
                ? result.type === 'jackpot' ? `🎉 JACKPOT! +$${result.win}` : result.type === 'triple' ? `🎰 TRIPLE! +$${result.win}` : result.type === 'double' ? `✨ DOUBLE! +$${result.win}` : `+$${result.win}`
                : '😔 No win — try again!'}
            </div>
          )}
          {result?.error && <div style={{ color: '#ef4444', fontSize: 13 }}>{result.error}</div>}
        </div>

        {/* Bet controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>BET:</span>
          {[5, 25, 50, 100, 250, 500].map(b => (
            <button key={b} onClick={() => setBet(b)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${bet === b ? '#f5c400' : '#333'}`, background: bet === b ? 'rgba(245,196,0,0.15)' : '#111', color: bet === b ? '#f5c400' : '#666', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s' }}>${b}</button>
          ))}
        </div>

        <button onClick={spin} disabled={spinning || disabled || balance < bet}
          style={{ width: '100%', padding: '14px', background: spinning ? '#333' : 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 12, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 900, color: '#0a0900', cursor: spinning || disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: spinning ? 'none' : '0 4px 16px rgba(245,196,0,0.35)' }}>
          {spinning ? '⟳ SPINNING...' : '🎰 SPIN!'}
        </button>
      </div>

      {/* Paytable */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 20px', width: '100%', maxWidth: 380 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', marginBottom: 10 }}>PAYTABLE</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {[['7️⃣ 7️⃣ 7️⃣','x50 — JACKPOT'],['💎 💎 💎','x10'],['⭐ ⭐ ⭐','x4'],['🔔 🔔 🔔','x3'],['Any Triple','x2'],['Any Double','x1.5']].map(([k,v])=>(
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{k}</span><span style={{ color: '#f5c400', fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ROULETTE GAME ────────────────────────────────────────────────────────────
const RED_NUMS = [1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36];

function RouletteGame({ balance, onPlay, disabled }) {
  const [bet, setBet] = useState(5);
  const [betType, setBetType] = useState('red');
  const [spinning, setSpinning] = useState(false);
  const [ballPos, setBallPos] = useState(0);
  const [result, setResult] = useState(null);
  const [displayNum, setDisplayNum] = useState(null);
  const canvasRef = useRef();

  useEffect(() => { drawWheel(ballPos); }, [ballPos]);

  const drawWheel = (angle) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cx = canvas.width / 2, cy = canvas.height / 2, r = cy - 8;
    const slots = 37;
    const arc = (2 * Math.PI) / slots;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < slots; i++) {
      const start = angle + i * arc;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, start + arc);
      const num = i === 0 ? 0 : i;
      ctx.fillStyle = num === 0 ? '#10b981' : RED_NUMS.includes(num) ? '#dc2626' : '#1f1f1f';
      ctx.fill();
      ctx.strokeStyle = '#f5c400';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(start + arc / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 9px Arial';
      ctx.fillText(num, r - 6, 3);
      ctx.restore();
    }
    // Center
    ctx.beginPath();
    ctx.arc(cx, cy, 16, 0, 2 * Math.PI);
    ctx.fillStyle = '#f5c400';
    ctx.fill();
    ctx.fillStyle = '#0a0900';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('A', cx, cy);
  };

  const spin = async () => {
    if (spinning || disabled || balance < bet) return;
    setSpinning(true); setResult(null); setDisplayNum(null);

    const extra = (Math.random() * 6 + 4) * 2 * Math.PI;
    const finalAngle = ballPos + extra;
    const duration = 3000;
    const start = performance.now();
    const startAngle = ballPos;

    const animate = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      const current = startAngle + (finalAngle - startAngle) * eased;
      setBallPos(current);
      drawWheel(current);
      if (t < 1) { requestAnimationFrame(animate); }
      else {
        // Get result from API
        api.post('/games/play', { game: 'roulette', betAmount: bet }).then(({ data }) => {
          setDisplayNum(data.details.number);
          setResult({ win: data.winAmount, number: data.details.number, color: data.details.color, balance: data.session.currentBalance });
          setSpinning(false);
          onPlay(data);
        }).catch(err => {
          setResult({ error: err.response?.data?.message || 'Error' });
          setSpinning(false);
        });
      }
    };
    requestAnimationFrame(animate);
  };

  const betOptions = [
    { key: 'red', label: '🔴 Red', odds: '2x' },
    { key: 'black', label: '⚫ Black', odds: '2x' },
    { key: 'even', label: '📊 Even', odds: '2x' },
    { key: 'odd', label: '🎲 Odd', odds: '2x' },
    { key: 'number', label: '🎯 Number', odds: '35x' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative' }}>
        <canvas ref={canvasRef} width={260} height={260} style={{ borderRadius: '50%', boxShadow: '0 0 30px rgba(245,196,0,0.25)' }} />
        {displayNum !== null && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: displayNum === 0 ? '#10b981' : RED_NUMS.includes(displayNum) ? '#dc2626' : '#1f1f1f', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 18, border: '2px solid #f5c400' }}>
            {displayNum}
          </div>
        )}
      </div>

      {result && !result.error && (
        <div style={{ textAlign: 'center', fontFamily: 'var(--font-display)', fontSize: result.win > 0 ? 20 : 14, color: result.win > 0 ? '#f5c400' : '#666' }}>
          {result.win > 0 ? `🎉 WIN! +$${result.win}` : `Number ${result.number} — No win`}
        </div>
      )}
      {result?.error && <div style={{ color: '#ef4444', fontSize: 13 }}>{result.error}</div>}

      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, justifyContent: 'center' }}>
          {betOptions.map(b => (
            <button key={b.key} onClick={() => setBetType(b.key)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${betType === b.key ? '#f5c400' : '#333'}`, background: betType === b.key ? 'rgba(245,196,0,0.12)' : '#111', color: betType === b.key ? '#f5c400' : '#888', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'all 0.15s' }}>
              {b.label} <span style={{ opacity: 0.6 }}>{b.odds}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>BET:</span>
          {[5, 25, 50, 100, 250, 500].map(b => (
            <button key={b} onClick={() => setBet(b)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${bet === b ? '#f5c400' : '#333'}`, background: bet === b ? 'rgba(245,196,0,0.15)' : '#111', color: bet === b ? '#f5c400' : '#666', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>${b}</button>
          ))}
        </div>

        <button onClick={spin} disabled={spinning || disabled || balance < bet}
          style={{ width: '100%', padding: '14px', background: spinning ? '#333' : 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 12, fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900, color: '#0a0900', cursor: spinning || disabled ? 'not-allowed' : 'pointer', boxShadow: spinning ? 'none' : '0 4px 16px rgba(245,196,0,0.35)' }}>
          {spinning ? '⟳ SPINNING...' : '🎡 SPIN WHEEL'}
        </button>
      </div>
    </div>
  );
}

// ─── BLACKJACK GAME ───────────────────────────────────────────────────────────
function BlackjackGame({ balance, onPlay, disabled }) {
  const [bet, setBet] = useState(5);
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [lastHand, setLastHand] = useState(null);

  const deal = async () => {
    if (playing || disabled || balance < bet) return;
    setPlaying(true); setResult(null);
    try {
      const { data } = await api.post('/games/play', { game: 'blackjack', betAmount: bet });
      setLastHand(data.details);
      setResult({ win: data.winAmount, result: data.details.result, balance: data.session.currentBalance });
      onPlay(data);
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Error' });
    } finally { setPlaying(false); }
  };

  const cardStyle = (flip = false) => ({
    width: 52, height: 72, borderRadius: 8, background: flip ? '#1a1400' : '#fff',
    border: `2px solid ${flip ? '#f5c400' : '#ccc'}`, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: flip ? 22 : 20, fontWeight: 700,
    color: flip ? '#f5c400' : '#000', boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
    transition: 'all 0.3s', flexShrink: 0,
  });

  const cardVal = (v) => v === 1 ? 'A' : v === 10 && Math.random() > 0.5 ? 'K' : v === 10 ? 'Q' : v;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, width: '100%', maxWidth: 420 }}>
      {/* Table */}
      <div style={{ background: 'radial-gradient(ellipse at center, #0d4a1a 0%, #072a0d 100%)', border: '3px solid #f5c400', borderRadius: 20, padding: '24px', width: '100%', boxShadow: '0 0 30px rgba(245,196,0,0.2)' }}>
        {/* Dealer */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8, fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
            DEALER {lastHand ? `— ${lastHand.dealerTotal}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {lastHand ? lastHand.dealerCards.map((c, i) => (
              <div key={i} style={cardStyle()}>{cardVal(c)}</div>
            )) : [1,2].map(i => <div key={i} style={cardStyle(true)}>🂠</div>)}
          </div>
        </div>

        <div style={{ height: 1, background: 'rgba(245,196,0,0.2)', margin: '10px 0' }} />

        {/* Player */}
        <div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8, fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
            YOU {lastHand ? `— ${lastHand.playerTotal}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {lastHand ? lastHand.playerCards.map((c, i) => (
              <div key={i} style={cardStyle()}>{cardVal(c)}</div>
            )) : [1,2].map(i => <div key={i} style={cardStyle(true)}>🂠</div>)}
          </div>
        </div>
      </div>

      {/* Result */}
      {result && !result.error && (
        <div style={{ textAlign: 'center', padding: '12px 24px', borderRadius: 12, background: result.win > bet ? 'rgba(245,196,0,0.12)' : result.result === 'push' ? 'rgba(100,100,100,0.12)' : 'rgba(239,68,68,0.1)', border: `1px solid ${result.win > bet ? '#f5c400' : result.result === 'push' ? '#555' : '#ef4444'}` }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: result.win > bet ? '#f5c400' : result.result === 'push' ? '#999' : '#ef4444' }}>
            {result.result === 'blackjack' ? '🃏 BLACKJACK! +$' + result.win :
             result.result === 'win' ? '✅ WIN! +$' + result.win :
             result.result === 'push' ? '🤝 PUSH — Bet returned' :
             '❌ DEALER WINS'}
          </div>
        </div>
      )}
      {result?.error && <div style={{ color: '#ef4444', fontSize: 13 }}>{result.error}</div>}

      {/* Bet + Deal */}
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>BET:</span>
          {[5, 25, 50, 100, 250, 500].map(b => (
            <button key={b} onClick={() => setBet(b)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${bet === b ? '#f5c400' : '#333'}`, background: bet === b ? 'rgba(245,196,0,0.15)' : '#111', color: bet === b ? '#f5c400' : '#666', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>${b}</button>
          ))}
        </div>
        <button onClick={deal} disabled={playing || disabled || balance < bet}
          style={{ width: '100%', padding: '14px', background: playing ? '#333' : 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 12, fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900, color: '#0a0900', cursor: playing || disabled ? 'not-allowed' : 'pointer', boxShadow: playing ? 'none' : '0 4px 16px rgba(245,196,0,0.35)' }}>
          {playing ? '⟳ DEALING...' : '🃏 DEAL CARDS'}
        </button>
      </div>
    </div>
  );
}

// ─── KENO GAME ────────────────────────────────────────────────────────────────
function KenoGame({ balance, onPlay, disabled }) {
  const [selected, setSelected] = useState(new Set());
  const [drawn, setDrawn] = useState(new Set());
  const [hits, setHits] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [bet, setBet] = useState(5);
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);

  const toggle = (n) => {
    if (playing || animating) return;
    if (selected.has(n)) { const s = new Set(selected); s.delete(n); setSelected(s); }
    else if (selected.size < 10) { setSelected(new Set(selected).add(n)); }
  };

  const play = async () => {
    if (playing || animating || disabled || balance < bet || selected.size === 0) return;
    setPlaying(true); setDrawn(new Set()); setHits([]); setResult(null);
    try {
      const { data } = await api.post('/games/play', { game: 'keno', betAmount: bet });
      const drawnNums = data.details.drawn;
      const hitNums = data.details.picked.filter(n => drawnNums.includes(n));

      // Animate draws
      setAnimating(true);
      let i = 0;
      const interval = setInterval(() => {
        setDrawn(d => new Set([...d, drawnNums[i]]));
        i++;
        if (i >= drawnNums.length) {
          clearInterval(interval);
          setHits(hitNums);
          setResult({ win: data.winAmount, hits: data.details.hits, balance: data.session.currentBalance });
          setPlaying(false);
          setAnimating(false);
          onPlay(data);
        }
      }, 80);
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Error' });
      setPlaying(false); setAnimating(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 480 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
        <span>Pick up to 10 numbers</span>
        <span style={{ color: '#f5c400', fontWeight: 700 }}>{selected.size}/10 selected</span>
      </div>

      {/* Number grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4, marginBottom: 16 }}>
        {Array.from({ length: 80 }, (_, i) => i + 1).map(n => {
          const isSel = selected.has(n);
          const isDrawn = drawn.has(n);
          const isHit = hits.includes(n);
          return (
            <button key={n} onClick={() => toggle(n)} style={{
              padding: '7px 0', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: playing ? 'default' : 'pointer',
              background: isHit ? '#10b981' : isDrawn ? 'rgba(245,196,0,0.2)' : isSel ? '#f5c400' : '#111',
              border: `1px solid ${isHit ? '#10b981' : isDrawn ? '#f5c400' : isSel ? '#f5c400' : '#333'}`,
              color: isHit ? '#fff' : isDrawn ? '#f5c400' : isSel ? '#0a0900' : '#666',
              transition: 'all 0.1s',
            }}>{n}</button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 12 }}>
        {[['#f5c400','#0a0900','Your picks'],['#10b981','#fff','Hits! 🎉'],['rgba(245,196,0,0.2)','#f5c400','Drawn']].map(([bg,c,label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, background: bg, border: '1px solid #555', borderRadius: 3 }} /><span style={{ color: 'var(--text-secondary)' }}>{label}</span></div>
        ))}
      </div>

      {result && !result.error && (
        <div style={{ textAlign: 'center', marginBottom: 14, fontFamily: 'var(--font-display)', fontSize: 18, color: result.win > 0 ? '#f5c400' : '#666', padding: '10px', background: result.win > 0 ? 'rgba(245,196,0,0.1)' : 'rgba(100,100,100,0.1)', borderRadius: 10 }}>
          {result.hits} Hits! {result.win > 0 ? `🎉 +$${result.win}` : '😔 No win'}
        </div>
      )}
      {result?.error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{result.error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>BET:</span>
        {[5, 25, 50, 100, 250, 500].map(b => (
          <button key={b} onClick={() => setBet(b)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${bet === b ? '#f5c400' : '#333'}`, background: bet === b ? 'rgba(245,196,0,0.15)' : '#111', color: bet === b ? '#f5c400' : '#666', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>${b}</button>
        ))}
      </div>
      <button onClick={play} disabled={playing || animating || disabled || selected.size === 0 || balance < bet}
        style={{ width: '100%', padding: '14px', background: (playing || animating || disabled) ? '#333' : 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 12, fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900, color: '#0a0900', cursor: (playing || animating || disabled || selected.size === 0) ? 'not-allowed' : 'pointer', boxShadow: (playing || animating) ? 'none' : '0 4px 16px rgba(245,196,0,0.35)' }}>
        {animating ? '⟳ DRAWING...' : playing ? '⟳ PLAYING...' : '▶ PLAY KENO'}
      </button>
    </div>
  );
}

// ─── MAIN GAMES PAGE ──────────────────────────────────────────────────────────
const GAMES_LIST = [
  { id: 'slots',     name: 'Slots',      icon: '🎰', desc: 'Classic 3-reel slot machine'  },
  { id: 'roulette',  name: 'Roulette',   icon: '🎡', desc: 'European roulette wheel'       },
  { id: 'blackjack', name: 'Blackjack',  icon: '🃏', desc: 'Beat the dealer to 21'        },
  { id: 'keno',      name: 'Keno',       icon: '🔢', desc: 'Pick numbers and match draws'  },
];

export default function Games() {
  const { user } = useAuth();
  const [activeGame, setActiveGame] = useState(null);
  const [session, setSession] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [redeemModal, setRedeemModal] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ gameUsername: '', platform: '' });
  const [redeemMsg, setRedeemMsg] = useState(null);
  const [myRedeems, setMyRedeems] = useState([]);
  const [loadingSession, setLoadingSession] = useState(true);
  const [gamesEnabled, setGamesEnabled] = useState(true);
  const [settings, setSettings] = useState({ minRedeem: 1000, redeemRate: 100 });

  const loadSession = useCallback(async () => {
    if (!user) return;
    setLoadingSession(true);
    try {
      const { data } = await api.get('/games/session');
      setSession(data.session);
      if (data.settings) {
        setGamesEnabled(data.settings.gamesEnabled !== false);
        setSettings(data.settings);
      }
    } catch {}
    finally { setLoadingSession(false); }
  }, [user]);

  const loadRedeems = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/games/my-redeems');
      setMyRedeems(data.redeems || []);
    } catch {}
  }, [user]);

  useEffect(() => { loadSession(); loadRedeems(); }, [loadSession, loadRedeems]);

  const collectDaily = async () => {
    setCollecting(true);
    try {
      const { data } = await api.post('/games/collect');
      setSession(data.session);
      alert(data.message);
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    } finally { setCollecting(false); }
  };

  const handlePlay = (data) => {
    setSession(prev => prev ? { ...prev, currentBalance: data.session.currentBalance, totalWon: data.session.totalWon } : prev);
  };

  const handleRedeem = async () => {
    try {
      const { data } = await api.post('/games/redeem', redeemForm);
      setRedeemMsg({ type: 'success', text: data.message });
      loadSession(); loadRedeems();
      setTimeout(() => { setRedeemModal(false); setRedeemMsg(null); }, 3000);
    } catch (err) {
      setRedeemMsg({ type: 'error', text: err.response?.data?.message || 'Error' });
    }
  };

  const canRedeem = session && parseFloat(session?.currentBalance || 0) >= parseFloat(settings?.minRedeem || 1000);
  const hasCollectedToday = session?.dailyCreditsCollected;
  const balance = session?.currentBalance ?? 0;
  const totalWon = session?.totalWon ?? 0;
  const freeplayPreview = Math.floor((balance / (settings?.minRedeem || 1000)) * ((settings?.redeemRate || 100) / 10));

  return (
    <div style={{ paddingTop: 70, minHeight: '100vh', background: 'var(--bg-primary)' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)', borderBottom: '1px solid var(--border)', padding: '40px 0 30px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,5vw,48px)', background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              🎰 FUN GAMES
            </h1>
            {/* Disclaimer */}
            <div style={{ display: 'inline-block', margin: '12px auto 0', background: 'rgba(245,196,0,0.08)', border: '1px solid rgba(245,196,0,0.25)', borderRadius: 12, padding: '10px 20px', maxWidth: 700 }}>
              <p style={{ color: '#f5c400', fontSize: 14, fontWeight: 600, margin: 0 }}>
                ⚠️ For Fun Only — NO REAL MONEY GAMES
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0' }}>
                Win Fun Credits and redeem them for real freeplay gifts! 🎁 $10,000 Fun Credits = $10 Freeplay
              </p>
            </div>
          </div>

          {/* Balance Dashboard */}
          {user ? (
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
              {/* Balance card */}
              <div style={{ background: 'var(--bg-card)', border: `1px solid ${balance >= 1000 ? '#10b981' : 'var(--border)'}`, borderRadius: 14, padding: '16px 24px', minWidth: 160, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', marginBottom: 6 }}>FUN BALANCE</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 900, color: balance >= 1000 ? '#10b981' : '#f5c400' }}>${balance.toLocaleString()}</div>
              </div>

              {/* Total won */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 24px', minWidth: 160, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em', marginBottom: 6 }}>TOTAL WON TODAY</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 900, color: '#f5c400' }}>${totalWon.toLocaleString()}</div>
              </div>

              {/* Action */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                {!hasCollectedToday ? (
                  <button onClick={collectDaily} disabled={collecting} className="btn btn-primary" style={{ fontSize: 15, padding: '12px 24px' }}>
                    {collecting ? '⟳ Collecting...' : '🎁 Collect $100 Daily Credits'}
                  </button>
                ) : (
                  <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '10px 18px', textAlign: 'center', fontSize: 13, color: '#10b981' }}>
                    ✅ Daily credits collected! Come back tomorrow.
                  </div>
                )}
                {canRedeem && (
                  <button onClick={() => setRedeemModal(true)} style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 10, padding: '12px 24px', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                    💎 Redeem ${balance.toLocaleString()} → ${freeplayPreview} Freeplay
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Link to="/login" className="btn btn-primary" style={{ fontSize: 16, padding: '12px 32px' }}>Login to Play & Earn →</Link>
            </div>
          )}
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px 80px' }}>

        {/* Games disabled banner */}
        {!gamesEnabled && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, marginBottom: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔴</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: '#ef4444', marginBottom: 12 }}>MINI GAMES OFFLINE</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15 }}>Mini Games are temporarily disabled by admin. Please check back later!</p>
          </div>
        )}

        {/* Game selector */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 36, maxWidth: 600, margin: '0 auto 36px' }}>
          {GAMES_LIST.map(g => (
            <button key={g.id} onClick={() => setActiveGame(activeGame === g.id ? null : g.id)}
              style={{ padding: '16px 10px', borderRadius: 14, border: `2px solid ${activeGame === g.id ? '#f5c400' : 'var(--border)'}`, background: activeGame === g.id ? 'rgba(245,196,0,0.08)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s', boxShadow: activeGame === g.id ? '0 0 20px rgba(245,196,0,0.2)' : 'none' }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>{g.icon}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: activeGame === g.id ? '#f5c400' : 'var(--text-secondary)', fontWeight: 700 }}>{g.name}</div>
            </button>
          ))}
        </div>

        {/* Active game */}
        {activeGame && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
            <div style={{ width: '100%', maxWidth: 520 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: '#f5c400' }}>
                  {GAMES_LIST.find(g => g.id === activeGame)?.icon} {GAMES_LIST.find(g => g.id === activeGame)?.name}
                </h2>
                {user && hasCollectedToday && (
                  <div style={{ fontSize: 13, color: balance > 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                    Balance: <span style={{ fontFamily: 'var(--font-display)' }}>${balance}</span>
                  </div>
                )}
              </div>

              {!user && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}><Link to="/login" className="btn btn-primary">Login to Play</Link></div>}
              {user && !hasCollectedToday && (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🎁</div>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>Collect your daily $100 fun credits to start playing!</p>
                  <button onClick={collectDaily} disabled={collecting} className="btn btn-primary" style={{ fontSize: 16 }}>
                    {collecting ? 'Collecting...' : '🎁 Collect $100 Now'}
                  </button>
                </div>
              )}
              {user && hasCollectedToday && balance <= 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>😔</div>
                  <p>You've run out of fun credits for today.</p>
                  <p style={{ fontSize: 13, marginTop: 8, color: 'var(--text-muted)' }}>Come back tomorrow for another $100!</p>
                </div>
              )}
              {user && hasCollectedToday && balance > 0 && (
                <>
                  {activeGame === 'slots'     && <SlotsGame     balance={balance} onPlay={handlePlay} disabled={false} />}
                  {activeGame === 'roulette'  && <RouletteGame  balance={balance} onPlay={handlePlay} disabled={false} />}
                  {activeGame === 'blackjack' && <BlackjackGame balance={balance} onPlay={handlePlay} disabled={false} />}
                  {activeGame === 'keno'      && <KenoGame      balance={balance} onPlay={handlePlay} disabled={false} />}
                </>
              )}
            </div>
          </div>
        )}

        {/* Conversion table */}
        <div style={{ maxWidth: 600, margin: '0 auto 48px' }}>
          <div className="card">
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: '#f5c400', marginBottom: 16 }}>💎 FUN CREDIT CONVERSION</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[1,2,5,10,50,100].map(m => {
                const fc = (settings?.minRedeem || 1000) * m;
                const fp = Math.floor((settings?.redeemRate || 100) / 10 * m);
                return (
                  <div key={fc} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>${fc.toLocaleString()} FC</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>= ${fp} Freeplay</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* My Redeem History */}
        {user && myRedeems.length > 0 && (
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 16, color: '#f5c400' }}>MY REDEEM HISTORY</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {myRedeems.map(r => (
                <div key={r._id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>${r.funCredits?.toLocaleString()} Fun Credits → ${r.freeplayValue} Freeplay</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: r.status === 'approved' ? 'rgba(16,185,129,0.15)' : r.status === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(245,196,0,0.12)', color: r.status === 'approved' ? '#10b981' : r.status === 'rejected' ? '#ef4444' : '#f5c400' }}>
                    {r.status === 'approved' ? '✅ Approved' : r.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Redeem Modal */}
      {redeemModal && (
        <div className="modal-overlay" onClick={() => setRedeemModal(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setRedeemModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: '#f5c400' }}>💎 REDEEM FUN CREDITS</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
              Your <strong style={{ color: '#f5c400' }}>${balance?.toLocaleString()} Fun Credits</strong> = <strong style={{ color: '#10b981' }}>${freeplayPreview} Real Freeplay</strong>
            </p>

            {redeemMsg && (
              <div style={{ background: redeemMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${redeemMsg.type === 'success' ? '#10b981' : '#ef4444'}`, borderRadius: 8, padding: '10px 14px', color: redeemMsg.type === 'success' ? '#10b981' : '#ef4444', marginBottom: 16, fontSize: 14 }}>
                {redeemMsg.text}
              </div>
            )}

            <div style={{ background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
              ⚠️ Once redeemed, your fun credits for today will be locked. Admin will verify and send your freeplay via Live Chat.
            </div>

            <div className="form-group">
              <label>Game Username (optional)</label>
              <input value={redeemForm.gameUsername} onChange={e => setRedeemForm({ ...redeemForm, gameUsername: e.target.value })} placeholder="Your in-game username" />
            </div>
            <div className="form-group">
              <label>Game Platform (optional)</label>
              <input value={redeemForm.platform} onChange={e => setRedeemForm({ ...redeemForm, platform: e.target.value })} placeholder="e.g. Fire Kirin, GameVault..." />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleRedeem} className="btn btn-primary" style={{ flex: 1 }}>✅ Submit Redeem Request</button>
              <button onClick={() => setRedeemModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
